import { useEffect, useRef } from 'react'
import Parse from 'parse'
import { usePermissionStore } from '../stores/permissionStore'
import { fetchAdminCapabilitiesWithRetry } from '../services/adminCapabilitiesService'
import { getStoredSessionToken } from '../utils/sessionToken'
import authService from '../services/authService'

/**
 * Loads server-attested elevated-admin flags after Parse session exists.
 * Must run near the app root so route guards and the main menu see verified flags.
 */
export function useAdminSessionCapabilities(): void {
  const ranForUserRef = useRef<string | null>(null)
  const userId = Parse.User.current()?.id ?? null

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      const sessionUser = await authService.waitForSessionCheck()
      if (cancelled) return

      const user = Parse.User.current()
      if (!user || !sessionUser) {
        ranForUserRef.current = null
        usePermissionStore.getState().setAnonymousAdminCapabilities()
        return
      }

      const sessionToken = getStoredSessionToken()
      if (!sessionToken) {
        usePermissionStore.getState().setAnonymousAdminCapabilities()
        return
      }

      if (ranForUserRef.current === user.id) {
        return
      }

      usePermissionStore.getState().beginAdminCapabilitiesFetch()

      const result = await fetchAdminCapabilitiesWithRetry(sessionToken)
      if (cancelled) return

      if (result.ok) {
        ranForUserRef.current = user.id
        usePermissionStore.getState().applyServerAdminCapabilities({
          meetsGlobalAdminRouteGate: result.meetsGlobalAdminRouteGate,
          meetsAdminAccessRouteGate: result.meetsAdminAccessRouteGate,
        })
        return
      }

      // Exhausted retries: fail closed for admin UI, unblock the rest of the app
      console.warn('[useAdminSessionCapabilities] Capabilities fetch failed after retries; admin UI disabled')
      ranForUserRef.current = user.id
      usePermissionStore.getState().applyServerAdminCapabilities({
        meetsGlobalAdminRouteGate: false,
        meetsAdminAccessRouteGate: false,
      })
    }

    void run()

    return () => {
      cancelled = true
    }
  }, [userId])
}
